import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import esLocale from "@fullcalendar/core/locales/es";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import CheckboxOnOff from "jsx/components/CitoPlus/components/CheckboxOnOff";
import {
  ZonaSocialSelect,
  useZonaSocialOptions,
} from "jsx/components/CitoPlus/components/ZonaSocial";

import axiosInstance from "../../../../../services/AxiosInstance";
import { notifyError, notifySuccess } from "../../components/CitoPlusToastr";
import {
  MultiSelectSelectVivienda,
  FIELD_LAYOUTS,
} from "jsx/components/CitoPlus/components/FieldBasic";
import ReservaGestionModal from "./ReservaGestionModal";
import BloqueoGestionModal from "./BloqueoGestionModal";

const CALENDARIO_URL = "/api/v3/zonas-sociales/reservas/calendario/";

const BLOQUEOS_CALENDARIO_URL = "/api/v3/zonas-sociales/bloqueos/calendario/";

const BLOCK_STATUS_OPTIONS = Object.freeze({
  all: { label: "Todos", value: "all" },
  active: { label: "Activos", value: "active" },
  inactive: { label: "Inactivos", value: "inactive" },
});

const blockStatusToBoolean = (status) => {
  if (status === "active") {
    return true;
  }
  if (status === "inactive") {
    return false;
  }
  return undefined;
};

const CalendarioReservasAdmin = () => {
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [blockEvents, setBlockEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const { zones, isLoading: isLoadingZones } = useZonaSocialOptions({
    onError: () =>
      notifyError("No fue posible obtener el listado de zonas sociales."),
  });
  const [filters, setFilters] = useState({
    zona: "all",
    estado: "all",
    unidad: "",
    unidadLabel: "",
  });
  const [blockFilters, setBlockFilters] = useState({
    status: "active",
  });
  const [showBlocks, setShowBlocks] = useState(true);
  const [unidadSelectKey, setUnidadSelectKey] = useState(0);
  const [calendarRange, setCalendarRange] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [blockModalState, setBlockModalState] = useState({
    isOpen: false,
    uuid: null,
    initialData: null,
  });
  const lastFetchSignatureRef = useRef(null);
  const blockFetchSignatureRef = useRef(null);

  const statusMetadata = useMemo(
    () => ({
      all: {
        label: "Todos los estados",
        color: "#4f5d75",
        variant: "secondary",
      },
      pendiente: {
        label: "Pendiente",
        color: "#f7b924",
        variant: "warning",
      },
      confirmada: {
        label: "Confirmada",
        color: "#2eb85c",
        variant: "success",
      },
      cancelada: {
        label: "Cancelada",
        color: "#d92550",
        variant: "danger",
      },
    }),
    [],
  );

  const parseBackendDate = useCallback((value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();

      const match = trimmedValue.match(
        /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/,
      );
      if (match) {
        const [, day, month, year, hour, minute, second] = match;
        const parsedDate = new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second),
        );
        if (!Number.isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }

      const directDate = new Date(trimmedValue);
      if (!Number.isNaN(directDate.getTime())) {
        return directDate;
      }
    }

    if (typeof value === "number") {
      const fromNumber = new Date(value);
      return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }

    const fallbackDate = new Date(value);
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }, []);

  const createUtcDate = useCallback(
    (year, month, day, hours = 0, minutes = 0, seconds = 0, milliseconds = 0) =>
      new Date(
        Date.UTC(year, month, day, hours, minutes, seconds, milliseconds),
      ),
    [],
  );

  const normalizeRangeForRequest = useCallback(
    (startValue, endValue) => {
      const startDate = parseBackendDate(startValue);
      const endDate = parseBackendDate(endValue);

      if (!startDate || !endDate) {
        return { start: null, end: null };
      }

      if (startDate.getTime() > endDate.getTime()) {
        return { start: endDate, end: startDate };
      }

      return { start: startDate, end: endDate };
    },
    [parseBackendDate],
  );

  const parseBooleanAttribute = useCallback((value) => {
    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "si", "sí"].includes(normalized)) {
        return true;
      }
      if (["false", "0", "no", "n"].includes(normalized)) {
        return false;
      }
    }

    if (typeof value === "number") {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
    }

    return Boolean(value);
  }, []);

  const normalizeReservationEvents = useCallback(
    (reservas) =>
      reservas
        .map((reserva) => {
          if (!reserva || !reserva.uuid) {
            return null;
          }

          const startDate =
            parseBackendDate(reserva.start) ||
            parseBackendDate(reserva.fecha_inicio);
          const endDate =
            parseBackendDate(reserva.end) ||
            parseBackendDate(reserva.fecha_fin);

          if (!startDate || !endDate) {
            return null;
          }

          const metadata = statusMetadata[reserva.estado] || statusMetadata.all;
          const allDay = parseBooleanAttribute(
            reserva.all_day ?? reserva.allDay ?? reserva.all_day_flag,
          );
          const startOriginal =
            reserva.start ?? reserva.fecha_inicio ?? startDate.toISOString();
          const endOriginal =
            reserva.end ?? reserva.fecha_fin ?? endDate.toISOString();

          return {
            id: reserva.uuid,
            title:
              reserva.titulo || reserva.zona_nombre || "Reserva sin título",
            start: startDate,
            end: endDate,
            allDay,
            backgroundColor: metadata.color,
            borderColor: metadata.color,
            extendedProps: {
              ...reserva,
              eventType: "reservation",
              allDay,
              startOriginal,
              endOriginal,
              start: startDate,
              end: endDate,
            },
          };
        })
        .filter(Boolean),
    [parseBackendDate, parseBooleanAttribute, statusMetadata],
  );

  const normalizeBlockEvents = useCallback(
    (bloqueos) =>
      bloqueos
        .map((bloqueo) => {
          if (!bloqueo || !bloqueo.uuid) {
            return null;
          }

          const startDate =
            parseBackendDate(bloqueo.fecha_inicio) ||
            parseBackendDate(bloqueo.start);
          const endDate =
            parseBackendDate(bloqueo.fecha_fin) ||
            parseBackendDate(bloqueo.end);

          if (!startDate || !endDate) {
            return null;
          }

          const isActive =
            typeof bloqueo.activo === "boolean" ? bloqueo.activo : true;
          const color = isActive ? "#d92550" : "#adb5bd";

          return {
            id: `block-${bloqueo.uuid}`,
            title: bloqueo.titulo || "Bloqueo programado",
            start: startDate,
            end: endDate,
            allDay: Boolean(bloqueo.all_day),
            backgroundColor: color,
            borderColor: color,
            classNames: [
              "calendar-block-event",
              isActive ? "calendar-block-active" : "calendar-block-inactive",
            ],
            extendedProps: {
              ...bloqueo,
              eventType: "block",
              isActive,
              sourceData: bloqueo,
            },
          };
        })
        .filter(Boolean),
    [parseBackendDate],
  );

  const fetchReservations = useCallback(
    async ({ start, end, zona, estado, unidad }, options = {}) => {
      const { start: normalizedStart, end: normalizedEnd } =
        normalizeRangeForRequest(start, end);

      if (!normalizedStart || !normalizedEnd) {
        return;
      }

      const { force = false } = options;
      const normalizedZona = zona ?? "all";
      const normalizedEstado = estado ?? "all";
      const normalizedUnidad = typeof unidad === "string" ? unidad.trim() : "";

      const signature = [
        normalizedStart.toISOString(),
        normalizedEnd.toISOString(),
        normalizedZona,
        normalizedEstado,
        normalizedUnidad,
      ].join("|");

      if (!force && lastFetchSignatureRef.current === signature) {
        return;
      }

      lastFetchSignatureRef.current = signature;

      setIsLoadingEvents(true);
      try {
        const params = {
          start: normalizedStart.toISOString(),
          end: normalizedEnd.toISOString(),
        };

        if (normalizedZona && normalizedZona !== "all") {
          params.zona = normalizedZona;
        }

        if (normalizedEstado && normalizedEstado !== "all") {
          params.estado = normalizedEstado;
        }

        if (normalizedUnidad) {
          params.unidad = normalizedUnidad;
        }

        const response = await axiosInstance.get(CALENDARIO_URL, { params });
        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setEvents(normalizeReservationEvents(payload));
      } catch (error) {
        lastFetchSignatureRef.current = null;
        setEvents([]);
        notifyError("No fue posible cargar las reservas del calendario.");
      } finally {
        setIsLoadingEvents(false);
      }
    },
    [normalizeRangeForRequest, normalizeReservationEvents],
  );

  const fetchBlocks = useCallback(
    async ({ start, end, zona, status }, options = {}) => {
      if (!showBlocks) {
        setBlockEvents([]);
        blockFetchSignatureRef.current = null;
        return;
      }

      const { start: normalizedStart, end: normalizedEnd } =
        normalizeRangeForRequest(start, end);

      if (!normalizedStart || !normalizedEnd) {
        return;
      }

      const resolvedStatus = status ?? "active";
      const statusBoolean = blockStatusToBoolean(resolvedStatus);
      const normalizedZona = zona ?? "all";

      const signature = [
        normalizedStart.toISOString(),
        normalizedEnd.toISOString(),
        normalizedZona,
        resolvedStatus,
      ].join("|");

      if (!options.force && blockFetchSignatureRef.current === signature) {
        return;
      }

      blockFetchSignatureRef.current = signature;

      setIsLoadingBlocks(true);
      try {
        const params = {
          start: normalizedStart.toISOString(),
          end: normalizedEnd.toISOString(),
        };

        if (normalizedZona && normalizedZona !== "all") {
          params.zona = normalizedZona;
        }

        if (typeof statusBoolean === "boolean") {
          params.activo = statusBoolean;
        }

        const response = await axiosInstance.get(BLOQUEOS_CALENDARIO_URL, {
          params,
        });
        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setBlockEvents(normalizeBlockEvents(payload));
      } catch (error) {
        blockFetchSignatureRef.current = null;
        setBlockEvents([]);
        notifyError("No fue posible cargar los bloqueos del calendario.");
      } finally {
        setIsLoadingBlocks(false);
      }
    },
    [normalizeBlockEvents, normalizeRangeForRequest, showBlocks],
  );

  useEffect(() => {
    if (!calendarRange) {
      return;
    }

    const rangeStart = calendarRange.start ?? calendarRange.startStr;
    const rangeEnd = calendarRange.end ?? calendarRange.endStr;

    if (!rangeStart || !rangeEnd) {
      return;
    }

    fetchReservations({
      start: rangeStart,
      end: rangeEnd,
      zona: filters.zona,
      estado: filters.estado,
      unidad: filters.unidad,
    });

    fetchBlocks(
      {
        start: rangeStart,
        end: rangeEnd,
        zona: filters.zona,
        status: blockFilters.status,
      },
      { force: true },
    );
  }, [
    calendarRange,
    fetchReservations,
    fetchBlocks,
    filters,
    blockFilters.status,
  ]);

  const handleDatesSet = useCallback(
    (args) => {
      const { view } = args;
      const baseInput =
        (view && view.currentStart) || args.start || args.startStr || null;

      let baseDate = null;

      if (baseInput instanceof Date) {
        baseDate = baseInput;
      } else if (typeof baseInput === "string") {
        const parsed = parseBackendDate(baseInput);
        if (parsed) {
          baseDate = parsed;
        }
      }

      if (view?.type === "dayGridMonth" && baseDate) {
        const monthStart = createUtcDate(
          baseDate.getUTCFullYear(),
          baseDate.getUTCMonth(),
          1,
        );
        const monthEnd = createUtcDate(
          baseDate.getUTCFullYear(),
          baseDate.getUTCMonth() + 1,
          0,
          23,
          59,
          59,
          999,
        );

        setCalendarRange({
          start: monthStart,
          end: monthEnd,
          startStr: monthStart.toISOString(),
          endStr: monthEnd.toISOString(),
        });
        return;
      }

      const normalizedRange = normalizeRangeForRequest(
        args.start ?? args.startStr,
        args.end ?? args.endStr,
      );

      if (normalizedRange.start && normalizedRange.end) {
        setCalendarRange({
          start: normalizedRange.start,
          end: normalizedRange.end,
          startStr: normalizedRange.start.toISOString(),
          endStr: normalizedRange.end.toISOString(),
        });
        return;
      }

      setCalendarRange({
        start: args.start || null,
        end: args.end || null,
        startStr: args.startStr || null,
        endStr: args.endStr || null,
      });
    },
    [createUtcDate, normalizeRangeForRequest, parseBackendDate],
  );

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleZonaFilterChange = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      zona: newValue || "all",
    }));
  }, []);

  const handleBlockStatusChange = (event) => {
    const { value } = event.target;
    setBlockFilters((prev) => ({ ...prev, status: value }));
  };

  const handleUnidadSelect = (selectedOptions) => {
    const normalizedSelection = Array.isArray(selectedOptions)
      ? selectedOptions
      : selectedOptions
        ? [selectedOptions]
        : [];

    const firstSelected = normalizedSelection[0] ?? null;

    if (!firstSelected) {
      setFilters((prev) => ({
        ...prev,
        unidad: "",
        unidadLabel: "",
      }));
      return;
    }

    const resolvedValue =
      firstSelected.value === null || firstSelected.value === undefined
        ? ""
        : String(firstSelected.value).trim();

    const resolvedLabel =
      firstSelected.label === null || firstSelected.label === undefined
        ? resolvedValue
        : String(firstSelected.label).trim();

    setFilters((prev) => ({
      ...prev,
      unidad: resolvedValue,
      unidadLabel: resolvedLabel,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      zona: "all",
      estado: "all",
      unidad: "",
      unidadLabel: "",
    });
    setBlockFilters({ status: "active" });
    setShowBlocks(true);
    setUnidadSelectKey((prev) => prev + 1);

    if (!calendarRange) {
      return;
    }

    const rangeStart = calendarRange.start ?? calendarRange.startStr;
    const rangeEnd = calendarRange.end ?? calendarRange.endStr;

    if (!rangeStart || !rangeEnd) {
      return;
    }

    fetchReservations(
      {
        start: rangeStart,
        end: rangeEnd,
        zona: "all",
        estado: "all",
        unidad: "",
      },
      { force: true },
    );

    fetchBlocks(
      {
        start: rangeStart,
        end: rangeEnd,
        zona: "all",
        status: "active",
      },
      { force: true },
    );
  };

  const refreshCurrentRange = useCallback(() => {
    if (!calendarRange) {
      return;
    }

    const rangeStart = calendarRange.start ?? calendarRange.startStr;
    const rangeEnd = calendarRange.end ?? calendarRange.endStr;

    if (!rangeStart || !rangeEnd) {
      return;
    }

    fetchReservations(
      {
        start: rangeStart,
        end: rangeEnd,
        zona: filters.zona,
        estado: filters.estado,
        unidad: filters.unidad,
      },
      { force: true },
    );

    fetchBlocks(
      {
        start: rangeStart,
        end: rangeEnd,
        zona: filters.zona,
        status: blockFilters.status,
      },
      { force: true },
    );
  }, [
    calendarRange,
    fetchReservations,
    fetchBlocks,
    filters.zona,
    filters.estado,
    filters.unidad,
    blockFilters.status,
  ]);

  const handleRefresh = () => {
    refreshCurrentRange();
  };

  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    const eventType = event.extendedProps?.eventType;

    if (eventType === "block") {
      setBlockModalState({
        isOpen: true,
        uuid: event.extendedProps?.uuid || event.id?.replace("block-", ""),
        initialData: event.extendedProps?.sourceData || null,
      });
      return;
    }

    const uuid = event.extendedProps?.uuid || event.id;
    setSelectedEvent({ uuid });
    setIsReservationModalOpen(true);
  };

  const handleModalClose = useCallback(() => {
    setIsReservationModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleBlockModalClose = useCallback(() => {
    setBlockModalState({
      isOpen: false,
      uuid: null,
      initialData: null,
    });
  }, []);

  const handleReservationModalUpdated = useCallback(() => {
    refreshCurrentRange();
  }, [refreshCurrentRange]);

  const handleBlockModalSaved = useCallback(() => {
    refreshCurrentRange();
    notifySuccess("Bloqueo guardado correctamente.");
  }, [refreshCurrentRange]);

  const handleCalendarSelect = useCallback(
    (selectionInfo) => {
      const calendarApi = calendarRef.current?.getApi();
      calendarApi?.unselect();

      if (!showBlocks) {
        notifyError(
          "Debes activar la visualización de bloqueos para poder crearlos desde el calendario.",
        );
        return;
      }

      const { start, end, allDay } = selectionInfo;

      setBlockModalState({
        isOpen: true,
        uuid: null,
        initialData: {
          titulo: "",
          descripcion: "",
          motivo: "mantenimiento",
          zona_uuid: filters.zona !== "all" ? filters.zona : "",
          fecha_inicio: start,
          fecha_fin: end,
          start,
          end,
          all_day: allDay,
          activo: true,
        },
      });
    },
    [filters.zona, showBlocks],
  );

  const finalEvents = useMemo(() => {
    if (!showBlocks) {
      return events;
    }
    return [...events, ...blockEvents];
  }, [events, blockEvents, showBlocks]);

  const isCalendarLoading = isLoadingEvents || (showBlocks && isLoadingBlocks);

  return (
    <section className="card">
      <style>
        {`
          .calendar-block-event {
            opacity: 0.5 !important;
          }
          .calendar-block-active {
            text-decoration: none;
          }
          .calendar-block-inactive {
            text-decoration: line-through;
          }
        `}
      </style>
      <header className="card-header">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <h2 className="card-title mb-0">Calendario de Reservas</h2>
            <div className="text-muted small">
              Gestiona reservas y bloqueos de zonas sociales desde un solo
              lugar.
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleResetFilters}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleRefresh}
              disabled={isCalendarLoading}
            >
              {isCalendarLoading ? (
                <>
                  <i
                    className="fas fa-circle-notch fa-spin me-2"
                    aria-hidden="true"
                  />
                  Actualizando...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-2" />
                  Actualizar
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() =>
                setBlockModalState({
                  isOpen: true,
                  uuid: null,
                  initialData: {
                    titulo: "",
                    descripcion: "",
                    motivo: "mantenimiento",
                    zona_uuid: filters.zona !== "all" ? filters.zona : "",
                    start: calendarRange?.start || new Date(),
                    end:
                      calendarRange?.end ||
                      new Date(Date.now() + 60 * 60 * 1000),
                    all_day: false,
                    activo: true,
                  },
                })
              }
            >
              <i className="fas fa-ban me-2" />
              Nuevo bloqueo
            </button>
          </div>
        </div>
      </header>

      <div className="card-body">
        <div className="row g-3 mb-4">
          <div className="col-md-6 col-lg-3">
            <label htmlFor="calendario-zona-filter" className="form-label">
              Zona social
            </label>
            <ZonaSocialSelect
              id="calendario-zona-filter"
              value={filters.zona}
              onChange={handleZonaFilterChange}
              allowAllOption
              allOptionValue="all"
              allOptionLabel="Todas las zonas"
              allowEmptyOption={false}
              options={zones}
              loading={isLoadingZones}
              autoFetch={false}
            />
          </div>

          <div className="col-md-6 col-lg-3">
            <label htmlFor="filter-estado" className="form-label">
              Estado de reserva
            </label>
            <select
              id="filter-estado"
              name="estado"
              className="form-select"
              value={filters.estado}
              onChange={handleFilterChange}
            >
              {Object.entries(statusMetadata).map(([key, metadata]) => (
                <option key={key} value={key}>
                  {metadata.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 col-lg-3">
            <label htmlFor="filter-block-status" className="form-label">
              Estado de bloqueos
            </label>
            <select
              id="filter-block-status"
              className="form-select"
              value={blockFilters.status}
              onChange={handleBlockStatusChange}
              disabled={!showBlocks}
            >
              {Object.values(BLOCK_STATUS_OPTIONS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="mt-2">
              <CheckboxOnOff
                id="toggle-show-blocks"
                name="toggle-show-blocks"
                label="Mostrar bloqueos en calendario"
                isChecked={showBlocks}
                onChange={(event) => {
                  setShowBlocks(event.target.checked);
                  if (!event.target.checked) {
                    setBlockEvents([]);
                  } else if (calendarRange) {
                    fetchBlocks(
                      {
                        start: calendarRange.start ?? calendarRange.startStr,
                        end: calendarRange.end ?? calendarRange.endStr,
                        zona: filters.zona,
                        status: blockFilters.status,
                      },
                      { force: true },
                    );
                  }
                }}
              />
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <MultiSelectSelectVivienda
              key={unidadSelectKey}
              onChange={handleUnidadSelect}
              label="Unidad (reservas)"
              showLabel
              layout={FIELD_LAYOUTS.VERTICAL}
              fieldClassName="form-group mb-0"
              contentClassName="w-100"
              selectClassName="w-100"
              placeholder="Selecciona una o más unidades"
            />
            {filters.unidadLabel && (
              <div className="form-text">
                Unidad filtrada: <strong>{filters.unidadLabel}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="position-relative">
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              listPlugin,
              interactionPlugin,
            ]}
            locale={esLocale}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,listWeek",
            }}
            height="auto"
            events={finalEvents}
            selectable
            selectMirror
            select={handleCalendarSelect}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
            dayMaxEventRows
            displayEventEnd
          />

          {isCalendarLoading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
              <div className="text-center">
                <SpinnerCitoPlus text="Cargando información del calendario..." />
              </div>
            </div>
          )}
        </div>

        {!isCalendarLoading && finalEvents.length === 0 && (
          <div className="alert alert-light border mt-4 mb-0">
            No encontramos reservas ni bloqueos para los filtros seleccionados.
          </div>
        )}
      </div>

      <ReservaGestionModal
        show={isReservationModalOpen}
        reservaUuid={selectedEvent?.uuid}
        onClose={handleModalClose}
        onUpdated={handleReservationModalUpdated}
        cachedZones={zones}
      />

      <BloqueoGestionModal
        show={blockModalState.isOpen}
        bloqueoUuid={blockModalState.uuid}
        onClose={handleBlockModalClose}
        onSaved={handleBlockModalSaved}
        cachedZones={zones}
        initialData={blockModalState.initialData}
      />
    </section>
  );
};

export default CalendarioReservasAdmin;
